import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getRemoteList,
  addRemoteToBusiness,
  updateRemote,
  deleteRemote,
} from "../../../../../../apis/Business/RemoteApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const RemoteTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownAppliance, setDropdownAppliance] = useState([
    { id: 1, name: t("Lamp"), value: t("Lamp") },
    { id: 2, name: t("Printer"), value: t("Printer") },
    { id: 3, name: t("Shredder"), value: t("Shredder") },
    { id: 4, name: t("Laptop"), value: t("Laptop") },
    { id: 5, name: t("Exstra screen"), value: t("Exstra screen") },
    { id: 6, name: t("Tablet"), value: t("Tablet") },
    {
      id: 7,
      name: t("Height adjustable table"),
      value: t("Height adjustable table"),
    },
    {
      id: 8,
      name: t("Smartphone"),
      value: t("Smartphone"),
    },
    {
      id: 9,
      name: t("Desktop computer"),
      value: t("Desktop computer"),
    },
  ]);

  useEffect(() => {
    setDropdownAppliance([
      { id: 1, name: t("Lamp"), value: t("Lamp") },
      { id: 2, name: t("Printer"), value: t("Printer") },
      { id: 3, name: t("Shredder"), value: t("Shredder") },
      { id: 4, name: t("Laptop"), value: t("Laptop") },
      { id: 5, name: t("Exstra screen"), value: t("Exstra screen") },
      { id: 6, name: t("Tablet"), value: t("Tablet") },
      {
        id: 7,
        name: t("Height adjustable table"),
        value: t("Height adjustable table"),
      },
      {
        id: 8,
        name: t("Smartphone"),
        value: t("Smartphone"),
      },
      {
        id: 9,
        name: t("Desktop computer"),
        value: t("Desktop computer"),
      },
    ]);
  }, [t]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getRemoteList({
      businessId,
      setObjects: setData,
    });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateRemote({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        appliance: editedItem["appliance.value"]
          ? editedItem["appliance.value"].value
          : editedItem["appliance"]
          ? editedItem["appliance"].value
          : null,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addRemoteToBusiness({
        businessId,
        ...newItem,
        appliance: newItem["appliance.value"]
          ? newItem["appliance.value"].value
          : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteRemote({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "employee",
        header: t("EMPLOYEE"),
        type: "text",
        disableSortBy: true,
        placeholder: t("NAME"),
        width: "120px",
      },
      {
        accessor: "appliance.value",
        header: `${t("APPLIANCES")}*`,
        type: "dropdown",
        dropdownData: dropdownAppliance,
        disableSortBy: true,
      },

      {
        accessor: "kwh",
        header: `${t("KWH_USAGE")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: t("EMPLOYEE_ELECTRICITY"),
        width: "90px",
        tipHeader: "Tip",
        tipBody: t("KWH_USAGE_TIP"),
      },
      {
        accessor: "hours_per_day",
        header: `${t("Hours per day")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: t("QTY"),
        width: "90px",
      },
      {
        accessor: "days_per_year",
        header: `${t("Days per year")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: t("QTY"),
        width: "90px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownAppliance, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "appliance.value": dropdownAppliance,
    }),
    [dropdownAppliance]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default RemoteTable;
